import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Package } from 'lucide-react';
import Header from '../components/Header';
import { getOrdersByEmail, getDesignDetailsByEmail } from '../lib/api';
import { toast } from 'sonner';
import { OrderCard } from '../components/dashboard/OrderCard';
import { DesignDetailsCard } from '../components/dashboard/DesignDetailsCard';

const EMAIL_KEY = 'thefavor:dashboardEmail';

const Dashboard = ({ cartItems, onRemoveFromCart, onUpdateQuantity }) => {
  const [emailInput, setEmailInput] = useState('');
  const [email, setEmail] = useState('');
  const [orders, setOrders] = useState([]);
  const [designDetails, setDesignDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async (e) => {
    if (!e) return;
    setEmail(e);
    setLoading(true);
    try {
      const [ordersRes, ddRes] = await Promise.all([
        getOrdersByEmail(e),
        getDesignDetailsByEmail(e),
      ]);
      setOrders(ordersRes.orders || []);
      setDesignDetails(ddRes.designDetails || []);
      sessionStorage.setItem(EMAIL_KEY, e);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      toast.error('Failed to load your data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Prefer email from lastOrder, fallback to stored
    const lastOrder = sessionStorage.getItem('lastOrder');
    let initialEmail = sessionStorage.getItem(EMAIL_KEY) || '';
    if (lastOrder) {
      try {
        const o = JSON.parse(lastOrder);
        const e = o?.customerInfo?.email || o?.email;
        if (e) initialEmail = e;
      } catch (err) {
        console.warn('Could not parse lastOrder from storage:', err);
      }
    }
    if (initialEmail) {
      setEmailInput(initialEmail);
      loadData(initialEmail);
    }
  }, [loadData]);

  const handleLookup = (e) => {
    e.preventDefault();
    if (!emailInput) return;
    loadData(emailInput.toLowerCase().trim());
  };

  const hasSubmittedDD = (orderNumber) =>
    designDetails.some((d) => d.orderNumber === orderNumber);

  return (
    <div className="min-h-screen bg-warm-ivory">
      <Header cartItems={cartItems} onRemoveFromCart={onRemoveFromCart} onUpdateQuantity={onUpdateQuantity} />

      <section className="pt-40 md:pt-44 pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-charcoal-ink mb-2">My Dashboard</h1>
            <p className="text-xl text-stone-taupe">Track your orders and design details</p>
          </div>

          {/* Email lookup */}
          <Card className="border-stone-taupe/20 mb-8">
            <CardHeader>
              <CardTitle className="text-charcoal-ink">View your orders</CardTitle>
              <CardDescription>Enter the email you used at checkout to access your orders.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLookup} className="flex flex-col sm:flex-row gap-3">
                <div className="flex-grow">
                  <Label htmlFor="dashboard-email" className="sr-only">Email</Label>
                  <Input
                    id="dashboard-email"
                    data-testid="dashboard-email-input"
                    type="email"
                    placeholder="you@example.com"
                    required
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    className="bg-white border-stone-taupe/20"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="dashboard-lookup-button"
                  disabled={loading}
                  className="bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory"
                >
                  {loading ? 'Loading...' : 'View Orders'}
                </Button>
              </form>
              {email && !loading && (
                <p className="text-sm text-stone-taupe mt-3">Showing orders for <strong>{email}</strong></p>
              )}
            </CardContent>
          </Card>

          {/* Orders Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-charcoal-ink mb-6">Your Orders</h2>
            {orders.length === 0 ? (
              <Card className="border-stone-taupe/20">
                <CardContent className="py-12 text-center">
                  <Package className="w-16 h-16 text-stone-taupe mx-auto mb-4" />
                  <p className="text-charcoal-ink mb-4">{email ? 'No orders found for this email' : 'Enter your email above to view orders'}</p>
                  <Link to="/">
                    <Button className="bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory" data-testid="dashboard-start-shopping-button">
                      Start Shopping
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <OrderCard
                    key={`${order.orderNumber}-${order.createdAt || order.sessionId || ''}`}
                    order={order}
                    submitted={hasSubmittedDD(order.orderNumber)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Design Details Section */}
          {designDetails.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-charcoal-ink mb-6">Design Details Submitted</h2>
              <div className="space-y-4">
                {designDetails.map((details, index) => (
                  <DesignDetailsCard key={details.detailsId || index} details={details} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
